import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
} from 'antd';
import dayjs from 'dayjs';
import { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import type { VoucherFormValues } from 'app/components/pages/Bookings/BookingDetails/_hooks/useVoucherActions';
import { useVoucherActions } from 'app/components/pages/Bookings/BookingDetails/_hooks/useVoucherActions';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import type {
  BookingDetails,
  BookingVoucherResponse,
} from 'app/typings/core/admin/bookings/booking';
import { VoucherReasonType } from 'app/typings/voucher';

import styles from './Modals.module.scss';

const RULES = [{ required: true, message: 'Required' }];
const { Option } = Select;

type VoucherProps = {
  bookingDetails: BookingDetails;
  voucher?: BookingVoucherResponse;
  isVoucherModalVisible: boolean;
  onCloseVoucherModal: () => void;
};

export const Voucher = ({
  bookingDetails,
  voucher,
  isVoucherModalVisible,
  onCloseVoucherModal,
}: VoucherProps) => {
  const { currencySymbol } = useContext(CurrencyContext);
  const { enterViewMode } = useContext(ViewEditModeContext);

  const [form] = Form.useForm<VoucherFormValues>();
  const formValues = Form.useWatch([], form);

  const { doesVoucherExist, onSave, onDelete, isMutating } = useVoucherActions({
    bookingDetails,
    voucher,
  });

  const onCancel = () => {
    form.resetFields();
    onCloseVoucherModal();
    enterViewMode();
  };

  const onSaveVoucher = async (values: VoucherFormValues) => {
    await onSave(values);
    onCancel();
  };

  const onDeleteVoucher = async () => {
    await onDelete();
    onCancel();
  };

  const initialValues: VoucherFormValues = voucher
    ? {
        value: Number(voucher.initialValue) ?? 0,
        expirationDate: voucher.expirationDate
          ? dayjs(voucher.expirationDate)
          : dayjs().add(1, 'year'),
        reasonType: voucher.reasonType,
        comment: voucher.comment,
      }
    : {
        value: 0,
        expirationDate: dayjs().add(1, 'year'),
        reasonType: undefined,
        comment: undefined,
      };

  const isButtonDisabled =
    !formValues?.reasonType ||
    !formValues?.value ||
    !formValues?.expirationDate ||
    isMutating;

  return (
    <Modal
      title={doesVoucherExist ? 'Edit voucher' : 'Create voucher'}
      open={isVoucherModalVisible}
      width={572}
      closable
      destroyOnClose
      onCancel={onCancel}
      centered
      className={styles.voucherModal}
      footer={
        <>
          {doesVoucherExist && (
            <Popconfirm
              title="Are you sure you want to delete this voucher ? (This action is irrevocable)"
              placement="bottomRight"
              onConfirm={onDeleteVoucher}
              okText="Yes"
              cancelText="No"
            >
              <Button>Delete</Button>
            </Popconfirm>
          )}
          <Button
            type="primary"
            disabled={isButtonDisabled}
            onClick={() => form.submit()}
          >
            {doesVoucherExist ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <Form
        className={styles.voucherForm}
        onFinish={onSaveVoucher}
        form={form}
        initialValues={initialValues}
        layout="vertical"
      >
        <FormLineLayout label={`Value (${currencySymbol})`} isRequired>
          <Form.Item name="value" rules={RULES}>
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() =>
              form.getFieldError('value').length > 0 && (
                <div className={styles.errorMessage}>
                  {form.getFieldError('value')[0]}
                </div>
              )
            }
          </Form.Item>
        </FormLineLayout>
        <FormLineLayout label="Expiry date" isRequired>
          <Form.Item name="expirationDate" rules={RULES}>
            <DatePicker disabled={!doesVoucherExist} format="YYYY-MM-DD" />
          </Form.Item>
        </FormLineLayout>
        <FormLineLayout label="Reason type" labelOnTop isRequired>
          <Form.Item name="reasonType" rules={RULES}>
            <Select placeholder="Choose a reason">
              {Object.entries(VoucherReasonType).map(([key, label]) => (
                <Option key={key} value={key}>
                  {label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </FormLineLayout>

        <FormLineLayout label="Comment" labelOnTop>
          <Form.Item name="comment" noStyle>
            <Input.TextArea placeholder="Optional" rows={4} />
          </Form.Item>
        </FormLineLayout>
      </Form>
    </Modal>
  );
};
